import React, { useCallback, useContext, useEffect, useState } from "react";
import { useScreenClass } from "@vp/swan";
import { CtaCustomData } from "../CtasContainer/CtasContainer";
import { PageStaticDataContext } from "../../../../contexts/PageStaticDataContextProvider";
import { ProductContext } from "../../../../contexts/ProductContextProvider";
import { BffDataContext } from "../../../../contexts/BffDataContextProvider";
import { getSiteflowGallery } from "../../utils/gallery";
import { CtaButton } from "./CtaButton";
import { PluginDataContext } from "../../../../pluginsInfrastructure/PluginDataContextProvider";
import { getProductCtaInfo, getCtaOnClickHandler } from "../../utils/ctaHelpers";
import { getScreenClass } from "../../../../utils/screenClass";
import { analyticsPageData } from "../../../../utils/analyticsConfig";
import useGetStudioDocumentUrl from "../../../../hooks/useGetStudioDocumentUrl";
import { FeedTemplateContext, FeedTemplateData } from "../../../../contexts/FeedTemplateProvider";

export interface GalleryCtaProps {
  customData: CtaCustomData;
}

const GalleryCta = (props: GalleryCtaProps) => {
  const {
    customData: {
      flow: { type, url, context },
      isPrimaryCta,
      buttonIndex,
      showDescription,
      isDesignUploaded,
      isLastCta,
    },
  } = props;
  const { pluginData } = useContext(PluginDataContext);
  const { locale, mpv, ctaOverrideEntries, tenant, productBuilderOptions } = useContext(
    PageStaticDataContext
  );
  const { studioDocumentUrl } = useGetStudioDocumentUrl();
  const {
    selections,
    selectedQuantity,
    clientSideStateInitialized,
    initialSelections,
    initialQuantity,
  } = useContext(ProductContext);
  const {
    compatibleValues,
    compatibleRanges,
    temporarilyOutOfStock,
    prices,
    initialPrices,
  } = useContext(BffDataContext);
  const [galleryUrl, setGalleryUrl] = useState<string>(url || "");
  const { isMobile } = getScreenClass(useScreenClass());
  const feedData = useContext(FeedTemplateContext);
  const galleryCtaEnabled = !temporarilyOutOfStock && !!galleryUrl;
  // eslint-disable-next-line prefer-const
  let { actualCtaText, actualCtaDescription, actualCtaIcon } = getProductCtaInfo(
    pluginData,
    ctaOverrideEntries,
    type,
    isDesignUploaded,
    isPrimaryCta
  );

  if (feedData) {
    actualCtaText = pluginData.contentful["ctaTextWithTemplate"] || "Explore similar templates";
    actualCtaDescription = "Choose from 5579 templates";
  }
  const onClickHandler = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) =>
      getCtaOnClickHandler({
        event,
        mpv,
        selections: selections || {},
        initialSelections,
        pageSection: analyticsPageData.pageSection,
        position: buttonIndex + 1,
        url: galleryUrl,
        linkType: type,
        initialQuantity,
        selectedQuantity,
        prices,
        productBuilderOptions,
        initialPrices,
        flowContexts: context,
        isDesignUploaded,
        compatibleRanges,
      }),
    [
      mpv,
      selections,
      initialSelections,
      analyticsPageData,
      buttonIndex,
      galleryUrl,
      type,
      initialQuantity,
      selectedQuantity,
      prices,
      productBuilderOptions,
      initialPrices,
      getCtaOnClickHandler,
    ]
  );

  useEffect((): (() => void) => {
    let isComponentMounted = true;

    async function updateCtaGalleryUrl() {
      if (url.includes("gallery.vistaprint")) {
        try {
          const newGalleryUrl = await getSiteflowGallery({
            tenant,
            locale,
            mpv,
            selections: selections || {},
            compatibleValues,
            compatibleRanges,
            quantity: selectedQuantity,
            categories: ctaOverrideEntries?.gallery?.categories || [],
            logError: false,
            isMobile,
            studioDocumentUrl,
          });

          // If we've already dismounted, then don't bother setting the state.
          if (isComponentMounted) {
            setGalleryUrl(newGalleryUrl || url);
          }
        } catch (err) {
          console.error("Failed to download Gallery URL", err);
        }
      } else {
        if (isComponentMounted) {
          setGalleryUrl(url);
        }
      }
    }

    if (clientSideStateInitialized && url) {
      updateCtaGalleryUrl();

      // This is the cleanup function that will ensure that we don't try to set state after
      // the component is dismounted.
      return () => (isComponentMounted = false);
    }

    return () => {};
  }, [mpv, locale, selections, selectedQuantity, clientSideStateInitialized, url, isMobile]);

  return (
    <CtaButton
      onClickHandler={onClickHandler}
      disabled={!galleryCtaEnabled}
      linkProperties={{
        href: addKeywordsQueryString(galleryUrl, feedData),
        openInNewTab: false,
      }}
      buttonKey={type}
      text={actualCtaText}
      description={showDescription ? actualCtaDescription : ""}
      iconComponent={actualCtaIcon}
      pageSection={analyticsPageData.pageSection}
      textButtonProps={{
        skin: isPrimaryCta ? "primary" : "secondary",
      }}
      addBottomSpace={!isLastCta}
      position={buttonIndex}
    />
  );
};

function addKeywordsQueryString(url: string, feedData: FeedTemplateData | undefined): string {
  if (feedData?.keywords) {
    return `${url}&keyword=${feedData.keywords}`;
  }
  return url;
}

export default GalleryCta;
